import { useState, useRef, useCallback, useEffect } from 'react';

// ============================================================================
// PERFORMANCE OPTIMIZATION: Throttle function
// ============================================================================
// Limits how often a function can be called (prevents 60+ calls per second)
const throttle = (func, delay) => {
  let timeoutId;
  let lastRan;
  
  return function(...args) {
    if (!lastRan) {
      func.apply(this, args);
      lastRan = Date.now();
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (Date.now() - lastRan >= delay) {
          func.apply(this, args);
          lastRan = Date.now();
        }
      }, delay - (Date.now() - lastRan));
    }
  };
};

// ============================================================================
// SCROLL CONTROLS HOOK - PERFORMANCE OPTIMIZED
// ============================================================================
export default function useScrollControls() {
  // Refs for scroll containers
  const categoryScrollRef = useRef(null);
  const productScrollRef = useRef(null);

  // Category scroll state
  const [categoryScrollState, setCategoryScrollState] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false
  });

  // Product scroll state
  const [productScrollState, setProductScrollState] = useState({
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false
  });

  // Check if arrows should show (window > 450px) - make it reactive
  const [shouldShowArrows, setShouldShowArrows] = useState(window.innerWidth > 450);

  // Update category scroll state
  const updateCategoryScrollState = useCallback(() => {
    if (!categoryScrollRef.current) {
      setCategoryScrollState({
        hasOverflow: false,
        canScrollLeft: false,
        canScrollRight: false
      });
      return;
    }

    const element = categoryScrollRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = element;
    
    // More generous overflow detection - add 5px tolerance
    const hasOverflow = scrollWidth > (clientWidth + 5);
    
    setCategoryScrollState({
      hasOverflow,
      canScrollLeft: scrollLeft > 5,
      canScrollRight: scrollLeft < (scrollWidth - clientWidth - 5)
    });
  }, []);

  // Update product scroll state
  const updateProductScrollState = useCallback(() => {
    if (!productScrollRef.current) {
      setProductScrollState({
        hasOverflow: false,
        canScrollLeft: false,
        canScrollRight: false
      });
      return;
    }

    const element = productScrollRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = element;
    
    // More generous overflow detection - add 10px tolerance for products
    const hasOverflow = scrollWidth > (clientWidth + 10);
    
    setProductScrollState({
      hasOverflow,
      canScrollLeft: scrollLeft > 5,
      canScrollRight: scrollLeft < (scrollWidth - clientWidth - 5)
    });
  }, []);

  // Category scroll functions
  const scrollCategoriesLeft = useCallback(() => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({ 
        left: -240, 
        behavior: 'smooth' 
      });
      setTimeout(updateCategoryScrollState, 300);
    }
  }, [updateCategoryScrollState]);

  const scrollCategoriesRight = useCallback(() => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({ 
        left: 240, 
        behavior: 'smooth' 
      });
      setTimeout(updateCategoryScrollState, 300);
    }
  }, [updateCategoryScrollState]);

  // Product scroll function
  const scrollProducts = useCallback((direction) => {
    if (productScrollRef.current) {
      const container = productScrollRef.current;
      // Card width (164px) + gap (10px = 2.5 * 4) = 174px
      // Scroll 2 cards at a time for better UX
      const cardWidth = 164;
      const gap = 10;
      const scrollAmount = (cardWidth + gap) * 2;

      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(updateProductScrollState, 300);
    }
  }, [updateProductScrollState]);

  // PERFORMANCE OPTIMIZATION: Trigger checks when content changes
  // Uses multiple attempts with proper cleanup
  const triggerCategoryCheck = useCallback(() => {
    const timers = [];
    
    // Multiple checks to catch dynamic content
    timers.push(setTimeout(updateCategoryScrollState, 50));
    timers.push(setTimeout(updateCategoryScrollState, 200));
    timers.push(setTimeout(updateCategoryScrollState, 500));
    
    // Return cleanup function
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [updateCategoryScrollState]);

  const triggerProductCheck = useCallback(() => {
    const timers = [];
    
    // Multiple checks to catch dynamic content
    timers.push(setTimeout(updateProductScrollState, 50));
    timers.push(setTimeout(updateProductScrollState, 200));
    timers.push(setTimeout(updateProductScrollState, 500));
    
    // Return cleanup function
    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [updateProductScrollState]);

  // Handle window resize and update shouldShowArrows
  useEffect(() => {
    const handleResize = () => {
      const newShouldShowArrows = window.innerWidth > 450;
      setShouldShowArrows(newShouldShowArrows);
      
      // Update scroll states after resize
      setTimeout(() => {
        updateCategoryScrollState();
        updateProductScrollState();
      }, 100);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateCategoryScrollState, updateProductScrollState]);

  // PERFORMANCE OPTIMIZATION: Add scroll event listeners with THROTTLING
  // This prevents the handlers from running 60+ times per second
  useEffect(() => {
    const categoryElement = categoryScrollRef.current;
    const productElement = productScrollRef.current;

    // Throttle to run maximum once every 100ms instead of 60+ times per second
    const handleCategoryScroll = throttle(() => {
      updateCategoryScrollState();
    }, 100);

    const handleProductScroll = throttle(() => {
      updateProductScrollState();
    }, 100);

    if (categoryElement) {
      categoryElement.addEventListener('scroll', handleCategoryScroll);
    }
    if (productElement) {
      productElement.addEventListener('scroll', handleProductScroll);
    }

    // IMPORTANT: Cleanup to prevent memory leaks
    return () => {
      if (categoryElement) {
        categoryElement.removeEventListener('scroll', handleCategoryScroll);
      }
      if (productElement) {
        productElement.removeEventListener('scroll', handleProductScroll);
      }
    };
  }, [updateCategoryScrollState, updateProductScrollState]);

  return {
    // Category scroll
    categoryScrollRef,
    categoryScrollState,
    scrollCategoriesLeft,
    scrollCategoriesRight,
    triggerCategoryCheck,

    // Product scroll
    productScrollRef,
    productScrollState,
    scrollProducts,
    triggerProductCheck,

    // Utility
    shouldShowArrows
  };
}